import React, { useState } from 'react';
import { motion } from 'motion/react';
import { CheckCircle, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { pricingPlans } from '../mock';
import Header from '../components/Header';
import Footer from '../components/Footer';

const springConfig = {
  type: "spring",
  damping: 80,
  stiffness: 400,
  mass: 1
};

const Pricing = () => {
  const [region, setRegion] = useState('IN');
  const filteredPlans = pricingPlans.filter(plan =>
    region === 'IN' ? plan.region === 'IN' : plan.region === 'GLOBAL'
  );
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-yellow-50 to-green-50">
        <div className="container mx-auto max-w-7xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Simple, <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Transparent Pricing</span>
            </h1>
            <p className="text-xl text-orange-600 max-w-3xl mx-auto mb-8">
              Choose the perfect plan for your child's learning journey. Start free, upgrade anytime!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          {/* Region Toggle */}
          <div className="flex justify-center mb-12">
            <div className="bg-gray-100 p-1 rounded-full inline-flex">
              <button
                onClick={() => setRegion('IN')}
                className={`px-8 py-2 rounded-full font-semibold transition-all ${region === 'IN'
                  ? 'bg-white text-orange-500 shadow-md'
                  : 'text-gray-500 hover:text-gray-900'
                  }`}
              >
                India
              </button>
              <button
                onClick={() => setRegion('GLOBAL')}
                className={`px-8 py-2 rounded-full font-semibold transition-all ${region === 'GLOBAL'
                  ? 'bg-white text-orange-500 shadow-md'
                  : 'text-gray-500 hover:text-gray-900'
                  }`}
              >
                Global
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {filteredPlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: index * 0.1 }}
              >
                <Card className={`p-8 h-full relative ${plan.highlighted
                  ? 'border-4 border-orange-500 shadow-2xl scale-105'
                  : 'border-2 border-orange-200 hover:border-orange-500 shadow-lg hover:shadow-2xl'
                  } transition-all duration-300`}>
                  {plan.highlighted && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </div>
                  )}
                  <h4 className="text-2xl font-bold mb-2 text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">{plan.name}</h4>
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-gray-900">
                      {plan.currency === 'INR' ? '₹' : '$'}{plan.price}
                    </span>
                    <span className="text-gray-500">/{plan.period}</span>
                  </div>

                  <div className="mb-6">
                    <p className="font-semibold text-gray-900 mb-2">{plan.subtitle}</p>
                    <p className="text-gray-600 leading-relaxed">{plan.description}</p>
                  </div>
                  <Button
                    className={`w-full rounded-full py-6 ${plan.highlighted
                      ? 'bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                      }`}
                  >
                    {plan.price === 0 ? 'Start Free' : 'Choose Plan'}
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Teaser */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h3 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500 mb-4">Have Questions?</h3>
          <p className="text-xl text-gray-600 mb-8">Check out our FAQ page for detailed answers</p>
          <Link to="/faq">
            <Button className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white rounded-full px-8 py-4">
              View FAQ
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;